import { useEffect, useRef, useCallback } from 'react';
import { useLogout } from '@sib/auth';
import { storage } from '@sib/utils';

const DEFAULT_TIMEOUT = 60 * 60 * 1000; // 1 hour
const ACTIVITY_CHECK_INTERVAL = 60 * 1000; // 1 minute

const ACTIVITY_EVENTS = [
  'mousemove',
  'mousedown',
  'keydown',
  'scroll',
  'touchstart',
];

export const useAutoLogout = ({
  timeout = DEFAULT_TIMEOUT,
  onLogout,
}: {
  timeout?: number;
  onLogout?: () => void;
} = {}) => {
  const { mutate: logout } = useLogout({ onLogout });

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const updateLastActive = useCallback(() => {
    storage.lastActive.set(Date.now());
  }, []);

  const checkInactivity = useCallback(() => {
    const lastActive = storage.lastActive.get() || 0;

    if (lastActive && Date.now() - lastActive >= timeout) logout();
  }, [timeout, logout]);

  const resetTimer = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);

    updateLastActive();

    timerRef.current = setTimeout(checkInactivity, timeout);
  }, [checkInactivity, updateLastActive, timeout]);

  useEffect(() => {
    ACTIVITY_EVENTS.forEach(event =>
      window.addEventListener(event, resetTimer)
    );

    const interval = setInterval(checkInactivity, ACTIVITY_CHECK_INTERVAL);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);

      ACTIVITY_EVENTS.forEach(event =>
        window.removeEventListener(event, resetTimer)
      );

      clearInterval(interval);
    };
  }, [resetTimer, updateLastActive, checkInactivity]);
};
